import {
  AUTH_TOKEN_SEARCH_PARAM,
  REGISTER_CODE_PARAM,
  REGISTER_DATA_PARAM,
  REGISTER_PIN_PARAM,
  REGISTER_SIGN_PARAM,
  STRIPE_SETUP_INTENT_CLIENT_SECRET_PARAM,
} from '../../config/routes';

// Analytics endpoint (uses same base URL as v1 API)
export const ANALYTICS_ENDPOINT = '/v1/stream/ingest';

// PostHog-only events - stay in PostHog, NOT sent to backend
export const POSTHOG_ONLY_EVENTS = [
  '$pageview',
  '$pageleave',
  '$rageclick',
  '$dead_click',
  '$exception',
  '$web_vitals',
  '$set',
  '$identify',
  '$feature_flag_called',
  '$groupidentify',
  '$create_alias',
  '$opt_in',
  '$session_start',
  '$autocapture',
  '$snapshot',
  '$$heatmap',
];

// Membership renewal funnel (see renewalStep.js). PostHog alerts on its failures.
export const RENEWAL_STEP_EVENT = 'renewal_step';

// Events that go to BOTH backend AND PostHog
export const DUAL_SEND_EVENTS = [RENEWAL_STEP_EVENT];

// Sensitive fields to redact from event properties before sending
export const SENSITIVE_PROPERTY_FIELDS = [
  'password', 'newPassword', 'oldPassword',
  'accessToken', 'refreshToken', 'idToken', 'authToken',
  // Firebase: the email-link sign-in body carries oobCode, and the securetoken
  // refresh response uses snake_case token names.
  'oobCode', 'access_token', 'refresh_token', 'id_token',
  'phoneNumber', 'phone',
  'pin', 'code', 'giftCardCode', 'redemptionCode', 'claimCode',
];

// PII fields to redact from network request/response bodies (recursive)
// Superset of SENSITIVE_PROPERTY_FIELDS + user profile PII
export const PII_NETWORK_FIELDS = new Set([
  ...SENSITIVE_PROPERTY_FIELDS,
  'email', 'firstName', 'lastName',
  'stripeCustomerId', 'affiliate_cid',
  'balance', 'currentBalance', 'pendingBalance', 'paidBalance', 'currentMonthPaidBalance',
  'cashbackMultiplierValue',
  'stores', 'segments',
]);

// Storepoint's locator reads its search term off `window.location.search`, so
// opening it on the bag's address means writing that address into the URL for
// the duration of one widget mount (see StoreLocatorSheet). Exported from here
// rather than owned by that component so the writer and the scrubbers below
// cannot drift apart.
export const LOCATE_QUERY_PARAM = 'locate';

// Query params whose VALUE is PII. Redacted from any URL we hand to a session
// recorder or an analytics `page_location` — unlike the body sanitizers, which
// see request payloads, nothing else scrubs the URL, so a param that carries a
// customer's street address would otherwise land in session replay verbatim.
export const PII_QUERY_PARAMS = new Set([
  LOCATE_QUERY_PARAM,
]);

// Firebase email-action links (sign-in, verify email, password reset) land on
// /register and /login carrying these. `oobCode` is a single-use credential:
// whoever holds it before it is consumed can complete the action. `continueUrl`
// is masked whole because it is itself a URL that can carry any of the params
// here. `mode` and `lang` are left readable: they are not secrets and they are
// how the email-link funnel is told apart in analytics.
export const FIREBASE_OOB_CODE_PARAM = 'oobCode';
export const FIREBASE_API_KEY_PARAM = 'apiKey';
export const FIREBASE_CONTINUE_URL_PARAM = 'continueUrl';

// Stripe appends this to the return_url after confirmPayment redirects.
export const STRIPE_PAYMENT_INTENT_CLIENT_SECRET_PARAM = 'payment_intent_client_secret';

export const GENERIC_TOKEN_QUERY_PARAM = 'token';

// Query params whose VALUE is a credential (a token, a one-time code, a signed
// payload). Same treatment as PII_QUERY_PARAMS — value masked, key and every
// other param kept — so a recording or analytics event still shows that the
// param was there.
export const SECRET_QUERY_PARAMS = new Set([
  FIREBASE_OOB_CODE_PARAM,
  FIREBASE_API_KEY_PARAM,
  FIREBASE_CONTINUE_URL_PARAM,
  GENERIC_TOKEN_QUERY_PARAM,
  AUTH_TOKEN_SEARCH_PARAM,
  REGISTER_CODE_PARAM,
  REGISTER_PIN_PARAM,
  REGISTER_SIGN_PARAM,
  // Encrypted with btoa(uid) as the passphrase, and the uid is the analytics
  // distinct_id, so it is readable by anyone who can see the event.
  REGISTER_DATA_PARAM,
  STRIPE_SETUP_INTENT_CLIENT_SECRET_PARAM,
  STRIPE_PAYMENT_INTENT_CLIENT_SECRET_PARAM,
]);

// Everything sanitizeUrl masks.
export const SENSITIVE_QUERY_PARAMS = new Set([
  ...PII_QUERY_PARAMS,
  ...SECRET_QUERY_PARAMS,
]);

export const REDACTED_VALUE = '**redacted**';

export const MAX_RETRIES = 3;
export const RETRY_DELAY_MS = 1000;
export const QUEUE_KEY = 'analytics_retry_queue';
export const MAX_QUEUE_SIZE = 100;
